import {AuthService} from 'compob2b-auth';
import {environment} from '../environments/environment';
import {ApiResponse, InitData, StoreService} from 'compob2b-libs';
import {Injector, TransferState} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';
import {Router} from '@angular/router';
import {firstValueFrom} from 'rxjs';
import {showCaseRoutes} from './cabinet/showcase.routing.module';
import {resolveIsShowcase} from './cabinet/shared/tokens/is-showcase.factory';
import {timeout} from 'rxjs/operators';
import {SEO_CONFIG_STATE_KEY} from './cabinet/shared/seo/seo-config.token';
import {SeoConfigClient} from './cabinet/shared/seo/seo-config.client';
import {forTransfer} from './cabinet/shared/seo/seo-config.transfer';

/**
 * Cabinet-вариант app initializer'а.
 * Showcase-сборка (CSR static) использует `COMPO_APP_INITIALIZER.showcase.ts`
 * через fileReplacement в angular.json `nextshowcase` config — там нет
 * SSR-handling'а /init.
 */

/**
 * Get tokens, menu and other settings for anonymous user
 * before application initialized
 * i.e. one user gave a link to another one and we need to
 * identify and authenticate another one as anonymous
 * if the user doesn't authorize
 * @param auth AuthService
 * @param platformId
 * @param storeService
 */
export function init(
  auth: AuthService,
  platformId: object,
  storeService: StoreService,
): () => Promise<ApiResponse<InitData> | Error | void> {
  return () => {
    // SSR: /init вытягивается синхронно перед рендером, чтобы siteName
    // и прочие settings были доступны в SeoMetaService / компонентах.
    // Кэшируется через cachedBackendInterceptor (TTL 10 мин на инстанс),
    // так что только первый SSR-рендер бьётся в backend, остальные мгновенны.
    if (!isPlatformBrowser(platformId)) {
      return firstValueFrom(storeService.initData()).catch(() => undefined);
    }
    // На showcase-домене анонимный токен/initCustomer не нужны: bundle ходит
    // только в публичные `/showcase/{hash}/...` через ShowcaseHashInterceptor.
    // Запуск anon-auth-flow дал бы лишний cross-origin POST с withCredentials,
    // который не обязан быть allowlist'ом на бэке для витрины.
    if (resolveIsShowcase(window.location.host, environment.showcaseUrl)) {
      return undefined;
    }
    if (environment.isOpenCabinet === true) {
      if (auth.isCustomer() || auth.isManager()) {
        return auth.initCustomer().toPromise();
      }

      const isAnonymous = auth.checkAndSetAnonymousToken();
      if (isAnonymous !== null) {
        return isAnonymous.toPromise();
      }
    }
  };
}

/**
 * Доставка SEO-конфига (канал описан в `seo-config.token.ts`). Ключ обязан быть заполнен
 * ДО первого рендера: конфиг читают синхронно не только `SeoMetaService`/
 * `CanonicalUrlBuilder`/`AnalyticsInjectorService`, но и экраны (`h1For`, мета главной
 * через `resolveHomeMeta`), а `SEO_CONFIG` снимает значение из TransferState при первом
 * инжекте — опоздавшее значение уже никто не увидит.
 *
 * Ключ на месте (приехал с SSR-гидратацией) → запроса нет. Пусто — значит гидратации не
 * было: CSR-сборка или client-render роут, и конфиг добираем сами. Ошибка/таймаут не валят
 * рендер: фабрика отдаст `SEO_CONFIG_FALLBACK`, то есть поведение «до фичи».
 */
export function initSeoConfig(
  transferState: TransferState,
  client: SeoConfigClient,
  timeoutMs: number,
): () => Promise<void> {
  return () => {
    if (transferState.hasKey(SEO_CONFIG_STATE_KEY)) {
      return Promise.resolve();
    }
    return firstValueFrom(client.fetch().pipe(timeout(timeoutMs)))
      .then((cfg) => transferState.set(SEO_CONFIG_STATE_KEY, forTransfer(cfg)))
      .catch(() => undefined);
  };
}

/**
 * Бюджет ожидания конфига. На сервере он делит 25-секундный SSR-дедлайн, в браузере —
 * это время белого экрана до первой отрисовки, поэтому вдвое короче: роботов на CSR всё
 * равно нет, а человеку лучше страница без метатегов, чем пустая.
 */
const SEO_CONFIG_TIMEOUT_SERVER_MS = 5_000;
const SEO_CONFIG_TIMEOUT_BROWSER_MS = 2_000;

/**
 * `init` и `initSeoConfig` одним провайдером, потому что в браузере между ними есть
 * зависимость: `/seo/config` закрыт anon-токеном (как `/catalog/tree` и `/product`), а
 * добывает его `init()`. Запущенные параллельно (как было), они давали гонку, в которой
 * запрос за конфигом уходил без `Authorization` и получал 403 — на CSR-стенде SEO-настройки
 * не применялись вообще. Примитива упорядочивания у Angular нет (все APP_INITIALIZER идут
 * через один `Promise.all`), поэтому порядок выражается композицией.
 *
 * Ждём только когда токена нет вовсе: тогда его выдаёт `init()`. У залогиненного и у
 * повторного визита токен уже в хранилище, и запросы независимы — незачем задерживать
 * первую отрисовку на RTT. На сервере зависимости нет вовсе: anon-Bearer навешивает
 * `anonAuthInterceptor`.
 *
 * Просроченный токен в хранилище (возврат после простоя) специально НЕ обрабатываем:
 * `init()` его не обновляет — обновление делает auth-интерцептор, и только увидев 403
 * (ветка BL-011: 403 + истёкший access-токен → прозрачный `/refresh/` и повтор запроса).
 * Проверено на живом стенде: `/seo/config` 403 → `/refresh/` 200 → `/seo/config` 200,
 * конфиг доезжает до первого рендера. Гейт «токен просрочен → ждать init» ничего не
 * меняет (init в этом случае no-op) — это была мёртвая ветка, её здесь нет намеренно.
 */
export function initApp(
  auth: AuthService,
  platformId: object,
  storeService: StoreService,
  transferState: TransferState,
  client: SeoConfigClient,
): () => Promise<void> {
  const browser = isPlatformBrowser(platformId);
  const core = init(auth, platformId, storeService);
  const seo = initSeoConfig(
    transferState,
    client,
    browser ? SEO_CONFIG_TIMEOUT_BROWSER_MS : SEO_CONFIG_TIMEOUT_SERVER_MS,
  );
  return async () => {
    // Showcase: SEO-модуля там нет намеренно, конфиг не нужен. Реальные showcase-сборки
    // подменяют весь файл, но cabinet-бандл может открыться и на showcase-хосте —
    // тогда запрос ушёл бы без токена и получил 403 (та же причина, что у init()).
    if (browser && resolveIsShowcase(window.location.host, environment.showcaseUrl)) {
      await core();
      return;
    }
    const started = core();
    if (browser && !auth.getToken()) {
      await started;
      await seo();
      return;
    }
    await Promise.all([started, seo()]);
  };
}

// Ставит роутеру нужное дерево: showcase или cabinet.
//
// На showcase-домене ставим только showCaseRoutes — cabinet routes из bundle'а
// никуда не деваются, но в роутере их быть не должно, иначе LayoutComponent
// перехватит '/' раньше ShowcaseLayoutComponent. Это и есть единственная
// оставшаяся причина существования этой функции.
//
// ИСТОРИЧЕСКОЕ (SEO-8, шаг 2): вторая причина была — сброс top-level конфига после
// того, как eager-цепочка `CabinetModule → ProductModule →
// ProductRoutingModule.forChild` накидывала туда свои роуты. `:uid` тогда ловил
// любой URL первым match'ем: `/catalog` уходил в ProductViewComponent с
// uid='catalog', и фронт делал GET /api/v1/product/catalog → 404 от бэка.
// `ProductModule`/`ProductRoutingModule` схлопнуты в lazy `product.routes.ts`, и
// `RouterModule.forChild`/`forRoot` в customer-приложении больше нет ни одного —
// источника утечки не осталось.
//
// ЧЕСТНО О ТЕКУЩЕМ СОСТОЯНИИ: `app.routes.ts` выбирает дерево тем же
// `resolveIsShowcase` и тем же `window.location.host`, и его результат уходит в
// `provideRouter(routes)`. То есть `resetConfig` здесь сейчас ставит РОВНО ТО ЖЕ
// самое — функция стала no-op'ом в обеих ветках. Не удалена сознательно: ровно это
// удаление уже вызывало регрессию NCP-470 (коммит af55e632d, см. шапку
// `COMPO_APP_INITIALIZER.spec.ts`), а проверяется оно только на живом
// showcase-домене (отдельная static-сборка + nginx), не юнит-тестом. Снятие — с
// такой проверкой, отдельной задачей.
export function loadRoutes(injector: Injector) {
  return () => {
    const isShowcase = typeof window !== 'undefined'
      && resolveIsShowcase(window.location.host, environment.showcaseUrl);
    // Гейт по ветке: на cabinet (и на SSR) `resetConfig` ставил РОВНО ТО ЖЕ дерево, что
    // уже отдал `provideRouter`, но `standardizeConfig` всё равно проходил по всем
    // ~40 узлам с детьми — и делал это на КАЖДОМ SSR-рендере, то есть на каждый запрос.
    // Ветка showcase остаётся нетронутой, поэтому showcase-поведение не меняется.
    if (isShowcase) {
      injector.get(Router).resetConfig(showCaseRoutes);
    }
  };
}

